import { useState, useEffect, useRef } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { portalFetch } from "@/lib/portalApi";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateSchemaNameFormat } from "@/lib/portalData";
import type { ValidateSchemaResponse } from "@/types/portal";

type SchemaValidationProps = {
  value: string;
  onChange: (value: string) => void;
  connectionString?: string;
  useManagedDatabase?: boolean;
  sourceType: string;
  orgName?: string;
  existingConnectorId?: number | null;
  existingSchemas?: string[];
  onValidationChange: (result: {
    validated: boolean;
    available: boolean;
  }) => void;
  disabled?: boolean;
  locked?: boolean;
  onUnlock?: () => void;
  /**
   * Generate a name whenever `sourceType` settles on a new value, unless the
   * user has typed their own name over the last generated one (web #1412).
   */
  autoGenerate?: boolean;
};

export function SchemaValidation({
  value,
  onChange,
  connectionString,
  useManagedDatabase,
  sourceType,
  orgName,
  existingConnectorId,
  existingSchemas = [],
  onValidationChange,
  disabled,
  locked,
  onUnlock,
  autoGenerate,
}: SchemaValidationProps) {
  const { token } = useAuth();
  // generate-schema and validate-schema are org admin and above in the API
  // (Steve, 2026-10-22), as Test Connection is (dp #2438). Both create forms
  // that render this already need a tested connection, which only an org
  // admin can run, so hiding these blocks no flow a member could finish.
  const { bypassesGroups: canValidateSchema } = usePermissions();
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidateSchemaResponse | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);
  // The last name Generate filled in, so auto-generate never overwrites a name
  // the user typed; and the latest request, so a slow reply for an old source
  // name cannot land after a newer one.
  const lastGenerated = useRef("");
  const generateRequest = useRef(0);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Clear validation when value changes
  useEffect(() => {
    setValidationResult(null);
    setFormatError(null);
    onValidationChange({ validated: false, available: false });
  }, [value, onValidationChange]);

  const handleValidate = async () => {
    // First, validate format client-side
    const formatErr = validateSchemaNameFormat(value);
    if (formatErr) {
      setFormatError(formatErr);
      onValidationChange({ validated: true, available: false });
      return;
    }
    setFormatError(null);

    setValidating(true);
    try {
      const body: Record<string, unknown> = {
        schemaName: value.trim(),
        excludeClientConnectorId: existingConnectorId ?? null,
      };
      if (useManagedDatabase) {
        // Check the managed default database too, not just the registry (web #1412).
        body.useManagedTarget = true;
      } else if (connectionString) {
        body.targetConnectionString = connectionString;
      }

      const result = await portalFetch<ValidateSchemaResponse>(
        "/api/v1/gateway/business2cloud/api/v1/database/validate-schema",
        {
          method: "POST",
          token: token ?? undefined,
          body,
        },
      );
      setValidationResult(result);
      onValidationChange({
        validated: true,
        available: result.isAvailable,
      });
    } catch (err) {
      setFormatError(err instanceof Error ? err.message : "Validation failed");
      onValidationChange({ validated: true, available: false });
    } finally {
      setValidating(false);
    }
  };

  // The managed database has no connection string: generate-schema then
  // checks the managed default target (web #1412).
  const canGenerate =
    canValidateSchema &&
    !!orgName &&
    sourceType.trim() !== "" &&
    (useManagedDatabase || !!connectionString);

  const handleGenerate = async () => {
    if (!canGenerate) return;

    const request = ++generateRequest.current;
    const before = valueRef.current;
    setGenerating(true);
    try {
      const body: Record<string, unknown> = { orgName, sourceType };
      if (!useManagedDatabase) {
        body.targetConnectionString = connectionString;
      }
      const result = await portalFetch<{ suggestedSchema: string }>(
        "/api/v1/gateway/business2cloud/api/v1/database/generate-schema",
        {
          method: "POST",
          token: token ?? undefined,
          body,
        },
      );
      if (request !== generateRequest.current) return;
      // The user typed while this was in flight: their name wins.
      if (valueRef.current !== before) return;
      lastGenerated.current = result.suggestedSchema;
      onChange(result.suggestedSchema);
    } catch (err) {
      if (request !== generateRequest.current) return;
      setFormatError(
        err instanceof Error
          ? `Could not generate a schema name: ${err.message}`
          : "Could not generate a schema name",
      );
    } finally {
      if (request === generateRequest.current) setGenerating(false);
    }
  };

  // Debounced so typing a source name sends one request, not one per key.
  useEffect(() => {
    if (!autoGenerate || !canGenerate || locked) return;
    const userTyped = () =>
      valueRef.current !== "" && valueRef.current !== lastGenerated.current;
    if (userTyped()) return;
    // Checked again when the timer fires: the user may type inside the window.
    const timer = setTimeout(() => {
      if (!userTyped()) void handleGenerate();
    }, 500);
    return () => clearTimeout(timer);
    // handleGenerate is recreated each render; sourceType is what triggers this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, canGenerate, locked, sourceType, orgName]);

  const handleUseSuggestion = () => {
    if (validationResult?.suggestedAlternative) {
      onChange(validationResult.suggestedAlternative);
    }
  };

  const canValidate =
    value.trim() !== "" &&
    (useManagedDatabase || (connectionString ?? "") !== "");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="schema-name">Schema Name</Label>
        <div className="flex gap-2">
          <Input
            id="schema-name"
            placeholder="e.g., nyp2601001"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || validating || locked}
            className={locked ? "bg-slate-50" : ""}
          />
          {locked ? (
            <Button
              type="button"
              variant="outline"
              onClick={onUnlock}
              disabled={disabled}
            >
              Change Schema
            </Button>
          ) : (
            <>
              {canGenerate && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={disabled || generating}
                  title="Generate schema name suggestion"
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              )}
              {canValidateSchema && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleValidate}
                  disabled={!canValidate || disabled || validating}
                >
                  {validating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Validate
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Must start with a letter and contain only letters, numbers,
          underscores, and hyphens.
        </p>
      </div>

      {/* Format error */}
      {formatError && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
          <p className="text-sm text-destructive">{formatError}</p>
        </div>
      )}

      {/* Validation result */}
      {validationResult && !formatError && (
        <>
          {validationResult.isAvailable ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Schema is available
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm text-amber-700">
                    {validationResult.conflictReason ||
                      "This schema is not available"}
                  </p>
                  {validationResult.suggestedAlternative && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-sm text-amber-700">
                        Suggested alternative:
                      </span>
                      <code className="rounded bg-amber-100 px-2 py-0.5 text-sm font-mono text-amber-800">
                        {validationResult.suggestedAlternative}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleUseSuggestion}
                        className="h-7 text-xs"
                      >
                        Use this
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Existing schemas reference */}
      {existingSchemas.length > 0 && !locked && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-700">
            Existing schemas in database:
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {existingSchemas.map((schema) => (
              <span
                key={schema}
                className="inline-flex items-center rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-700"
              >
                {schema}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
